import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from 'recharts';
import { format, parseISO } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { moduleLabel } from '@/lib/usageTracker';
import { usageFetch, rangeQuery, fmtMinutes, fmtAgo } from './usageApi';

const ModuleList = ({ modules }) => {
  const max = Math.max(1, ...modules.map((m) => m.minutes));
  return (
    <div className="space-y-2" data-testid="user-timeline-modules">
      {modules.map((m) => (
        <div key={m.module}>
          <div className="flex justify-between text-xs mb-1">
            <span className="font-medium text-slate-700">{moduleLabel(m.module)}</span>
            <span className="text-slate-500 tabular-nums">{fmtMinutes(m.minutes)} · {m.views} views</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100">
            <div className="h-1.5 rounded-full bg-[#2EB4AC]" style={{ width: `${(m.minutes / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const EventList = ({ events }) => (
  <ol className="relative border-l border-slate-200 ml-1.5 space-y-3" data-testid="user-timeline-events">
    {events.map((e, i) => (
      <li key={i} className="ml-4">
        <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#2EB4AC]" />
        <div className="text-sm font-medium text-slate-800">{moduleLabel(e.module)}</div>
        <div className="text-xs text-slate-400">{format(new Date(e.ts), 'EEE d MMM, h:mm a')} · <span className="font-mono">{e.path}</span></div>
      </li>
    ))}
  </ol>
);

export const UserTimelineSheet = ({ user, range, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!user) return;
    setData(null);
    usageFetch(`/users/${user.user_id}/timeline${rangeQuery(range)}`).then(setData).catch(() => setData({ error: true }));
  }, [user, range]);

  return (
    <Sheet open={!!user} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="user-timeline-sheet">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <Avatar className="h-11 w-11"><AvatarImage src={user?.picture} /><AvatarFallback>{(user?.name || '?')[0]}</AvatarFallback></Avatar>
            <div>
              <SheetTitle data-testid="user-timeline-name">{user?.name}</SheetTitle>
              <SheetDescription>{user?.email} · last seen {fmtAgo(data?.last_seen || user?.last_seen)}</SheetDescription>
            </div>
          </div>
        </SheetHeader>
        {!data ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#2EB4AC]" /></div>
        ) : data.error ? (
          <p className="py-10 text-center text-sm text-red-500">Could not load timeline</p>
        ) : (
          <div className="mt-6 space-y-7">
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Daily active time</h4>
              <div className="h-36">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.daily}>
                    <XAxis dataKey="date" tickFormatter={(d) => format(parseISO(d), 'd MMM')} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} minTickGap={20} />
                    <Tooltip labelFormatter={(d) => format(parseISO(d), 'EEE, d MMM')} formatter={(v) => [fmtMinutes(v), 'Active']} contentStyle={{ borderRadius: 10, fontSize: 12 }} cursor={{ fill: '#f1f5f9' }} />
                    <Bar dataKey="active_minutes" fill="#2EB4AC" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </section>
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Most-used modules</h4>
              {data.modules.length ? <ModuleList modules={data.modules} /> : <p className="text-sm text-slate-400">No module activity</p>}
            </section>
            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Recent activity <span className="normal-case font-normal">(last 200 page visits)</span></h4>
              {data.events.length ? <EventList events={data.events} /> : <p className="text-sm text-slate-400">No page visits recorded in this range</p>}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
